import { useDistributorConflictDetection } from '@/hooks/swapRoles/useDistributorConflictDetection';
import { type SwapModalData, useDistributorSwapModal } from '@/hooks/swapRoles/useDistributorSwapModal';
import { useManageDistributorGroupUsers } from '@/hooks/useManageDistributorGroupUsers';
import { useSnackbar } from '@/hooks/useSnackbar';

interface UseDistributorRoleManagementReturn {
  handleRoleChange: (userId: string, groupId: number, groupName: string, newRole: string) => Promise<void>;
  swapModalOpen: boolean;
  swapModalData: SwapModalData | null;
  closeSwapModal: () => void;
  confirmSwap: () => Promise<void>;
  isRoleChanging: boolean;
  isSwappingRole: boolean;
}

export function useDistributorRoleManagement(): UseDistributorRoleManagementReturn {
  const snackbar = useSnackbar();

  const { updateUser, isMutating: isUpdatingRole } = useManageDistributorGroupUsers({ snackbar });
  const { checkDistributorConflict } = useDistributorConflictDetection();
  const { swapModalOpen, swapModalData, openSwapModal, closeSwapModal, confirmSwap, isSwappingRole } = useDistributorSwapModal();

  const handleRoleChange = async (userId: string, groupId: number, groupName: string, newRole: string) => {
    if (isUpdatingRole) {
      return;
    }

    try {
      // Special handling for Distributor role - check for conflicts
      if (newRole === 'Distributor') {
        const conflict = await checkDistributorConflict(userId, groupId);

        if (conflict.hasConflict) {
          openSwapModal({
            userId,
            userEmail: userId,
            currentGroupId: conflict.conflictGroupId!,
            currentGroupName: conflict.conflictGroupName!,
            currentGroupType: conflict.conflictGroupType,
            newGroupId: groupId,
            newGroupName: groupName,
          });

          return;
        }
      }

      // Execute role change using updateUser
      const result = await updateUser(groupId, userId, newRole);

      if (result) {
        snackbar.showSuccess('User role updated successfully');
      }
    } catch (err) {
      // Error handling is already done in useManageDistributorGroupUsers
      console.error('Failed to update user role:', err);
    }
  };

  return {
    handleRoleChange,
    swapModalOpen,
    swapModalData,
    closeSwapModal,
    confirmSwap,
    isRoleChanging: isUpdatingRole,
    isSwappingRole,
  };
}
